import { useCallback, Suspense, useEffect } from 'react'

import { Input, SkeletonBox } from '@circlefin/components'
import { useForm, y } from '@circlefin/form'
import { useDebounce } from '@circlefin/hooks'
import { SearchDocument } from '@features/algolia.graphql'
import { useLoadableQuery } from '@shared/apollo'

import { SearchResults } from './SearchResults'

import type { SubmitHandler } from '@circlefin/form/useForm'

const schema = y.object().shape({
  search: y.string(),
})
type FormSchema = y.InferType<typeof schema>

/**
 * Props for the SearchSection component.
 */
export type SearchSectionProps = {
  /**
   * Initial value to search for.
   */
  searchTerm?: string
}

export const SearchSection: React.FC<SearchSectionProps> = ({ searchTerm }) => {
  const [Form, { handleSubmit: triggerSubmit, setValue }] = useForm<FormSchema>(
    { schema },
  )

  const [search, searchRef] = useLoadableQuery(SearchDocument)

  const handleSubmit: SubmitHandler<FormSchema> = useCallback(
    (values) => {
      if (typeof values.search === 'string') {
        search({ input: { searchKey: values.search } })
      }
    },
    [search],
  )

  const handleChange = useDebounce({
    callback: () => {
      void triggerSubmit(handleSubmit, () => null)()
    },
    delay: 1000,
  })

  useEffect(() => {
    setValue('search', searchTerm)
    handleSubmit({ search: searchTerm })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm])

  return (
    <Form onSubmit={handleSubmit}>
      <Form.Input className="w-full" name="search" onChange={handleChange}>
        <Input.Inline.Icon
          align="left"
          data-testid="input-icon"
          name="SearchOutline"
        />
      </Form.Input>
      <Suspense fallback={SearchSkeleton}>
        {searchRef ? <SearchResults queryRef={searchRef} /> : SearchSkeleton}
      </Suspense>
    </Form>
  )
}

const skeletonItems = ['a', 'b', 'c', 'd']
export const SearchSkeleton = (
  <>
    <SkeletonBox className="mb-4 mt-6  h-8 w-1/2" loading />
    <div className="grid grid-flow-row grid-cols-1 gap-4 sm:grid-cols-2">
      {skeletonItems.map((i) => (
        <SkeletonBox key={i} className="flex min-h-48 flex-col" loading />
      ))}
    </div>
  </>
)
